import { PartnershipBillingType } from '@/pages/donor-scheduling-partnership/components/partnership-form/constants';
import { ChutePartnershipSummary } from '@/services/chute-scheduler/types';

export type PartnershipPartner = {
  key: string;
  name: string;
  percent: number;
};

const SLOTS = [1, 2, 3, 4] as const;

const formatPercent = (value: number): string =>
  Number.isInteger(value) ? `${value}` : `${parseFloat(value.toFixed(2))}`;

export const formatPartnershipPercent = formatPercent;

export const getPartnershipPartners = (
  partnership: ChutePartnershipSummary | null | undefined,
): PartnershipPartner[] => {
  if (!partnership) return [];

  const partners: PartnershipPartner[] = [];
  const indexByKey = new Map<string, number>();

  SLOTS.forEach((slot) => {
    const id = partnership[`account${slot}Id`] as string | null;
    const name = partnership[`account${slot}`] as string | null;
    if (!id && !name) return;

    const key = id ?? (name as string);
    const percent = partnership[`percent${slot}`] as number | null;
    if (!percent) return;

    const existing = indexByKey.get(key);

    if (existing === undefined) {
      indexByKey.set(key, partners.length);
      partners.push({ key, name: name ?? key, percent });
      return;
    }

    partners[existing].percent += percent;
  });

  return partners.sort((a, b) => b.percent - a.percent);
};

export const getPartnershipRatio = (
  partners: PartnershipPartner[],
): string | null => {
  if (partners.length === 0) return null;
  const total = partners.reduce((sum, partner) => sum + partner.percent, 0);
  if (total === 0) return null;
  return partners.map((partner) => formatPercent(partner.percent)).join('/');
};

export const isSpecialBillingPartnership = (
  partnership: ChutePartnershipSummary | null | undefined,
): boolean =>
  partnership?.billingType === PartnershipBillingType.SPECIAL_PARTNERSHIP;

export const getPartnershipOptions = (
  partnership: ChutePartnershipSummary | null | undefined,
): { slot: number; account: string }[] => {
  if (!partnership) return [];
  return SLOTS.reduce<{ slot: number; account: string }[]>((options, slot) => {
    const name = partnership[`account${slot}`] as string | null;
    if (name) options.push({ slot, account: name });
    return options;
  }, []);
};

export const formatPartnership = (
  partnership: ChutePartnershipSummary | null | undefined,
): string => {
  const partners = getPartnershipPartners(partnership);
  if (partners.length === 0) return '';

  const accounts = partners
    .map((partner) => `${partner.name} (${formatPercent(partner.percent)}%)`)
    .join(', ');

  const ratio = getPartnershipRatio(partners);

  return ratio ? `${ratio} - ${accounts}` : accounts;
};
